/**
 * EE CONFIGURATOR — Emporium Embroidery product definitions + submission.
 * Product specs follow the client's website notes (Aug 2026 PDF, page 2 mockup).
 * Submission reuses the quote-tool plumbing: Supabase `quotes` table +
 * `quote-files` storage + a Gmail alert via EE-admin + Cloudflare Turnstile.
 */
(function () {
  'use strict';

  // New-quote alerts go through EE-admin, which already sends as
  // office@emporiumembroidery.co.uk over the Gmail API and threads replies back
  // into the dashboard. Only the quote id is sent; the email is built server-side.
  var SUBMIT_URL = 'https://quote.emporiumembroidery.co.uk/api/quote-submit';
  var SUPABASE_URL  = 'https://tnqgexdzhorukbiexjlp.supabase.co';
  var SUPABASE_KEY  = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRucWdleGR6aG9ydWtiaWV4amxwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY3NTkyMjEsImV4cCI6MjA5MjMzNTIyMX0.wfhQRipyxEVK3VIvPEts5K4oqhLRaNt5P_LR3qhHVSI';

  var CDN = 'https://cdn.shopify.com/s/files/1/1059/7505/4675/files/';

  /* ── Shared option sets ─────────────────────────────────────────── */

  var FABRICS = [
    { name: 'Black',        hex: '#111111' },
    { name: 'Navy',         hex: '#1F2A44' },
    { name: 'Bottle Green', hex: '#14452F' },
    { name: 'Red',          hex: '#A31621' },
    { name: 'Tan',          hex: '#C3A278' },
    { name: 'Grey',         hex: '#8A8D91' },
    { name: 'White',        hex: '#F4F1EA' },
    { name: 'Royal Blue',   hex: '#1D4ED8' },
    { name: 'Sky Blue',     hex: '#7FB2E5' },
    { name: 'Yellow',       hex: '#E7C24A' },
    { name: 'Orange',       hex: '#D97A26' },
    { name: 'Purple',       hex: '#5B3A8E' },
    { name: 'Pink',         hex: '#D98CA6' },
    { name: 'Maroon',       hex: '#6E1F2E' },
    { name: 'Brown',        hex: '#6B4A2F' },
    { name: 'Charcoal',     hex: '#3A3D40' },
  ];

  var THREADS = [
    { name: 'Black',      hex: '#111111' },
    { name: 'White',      hex: '#F4F1EA' },
    { name: 'Grey',       hex: '#8A8D91' },
    { name: 'Silver',     hex: '#C9CDD3' },
    { name: 'Red',        hex: '#B31E2C' },
    { name: 'Orange',     hex: '#E0762B' },
    { name: 'Gold',       hex: '#D8A93C' },
    { name: 'Yellow',     hex: '#EFCB44' },
    { name: 'Emerald',    hex: '#1E7A4F' },
    { name: 'Bottle Green', hex: '#14452F' },
    { name: 'Royal Blue', hex: '#1D4ED8' },
    { name: 'Navy',       hex: '#1F2A44' },
    { name: 'Sky Blue',   hex: '#7FB2E5' },
    { name: 'Purple',     hex: '#5B3A8E' },
    { name: 'Pink',       hex: '#D98CA6' },
    { name: 'Brown',      hex: '#6B4A2F' },
  ];

  var BACKINGS = ['Sew-on', 'Iron-on', 'Adhesive', 'Velcro hook only', 'Velcro hook and loop', 'Premium felt sew-on'];
  var SPECIAL_THREADS = ['Metallic', 'Glow in the dark', 'Reflective', 'Fire resistant'];
  var COLOUR_AMOUNTS = ['3 colours', '6 colours', '9 colours', '12 colours', '12+ colours'];
  var FONTS = ['Block', 'Athletic Block', 'Serif', 'Script', 'Brush Script', 'Old English', 'Typewriter', 'Varsity', 'Other — specify in notes'];
  var MERROWED_NOTE = 'Merrowed border option only available on quantity over 50 units and basic shapes (circular, square, rectangular, triangular or shield-like).';

  function merrowedField() {
    return {
      id: 'merrowed', type: 'radio', label: 'Merrowed Border',
      help: MERROWED_NOTE,
      options: ['Yes', 'No'], default: 'No', disabledValue: 'No',
      minQty: 50, minQtyHint: 'Available on quantities over 50 units only.',
    };
  }
  function uploadField() {
    return { id: 'artwork', type: 'upload', label: 'Upload Image (PNG, JPEG)', maxSizeMB: 20, maxFiles: 3 };
  }
  function notesField(ph) {
    return { id: 'notes', type: 'textarea', label: 'Notes', maxLength: 250, placeholder: ph || 'Add text, change artwork shape, anything specific…' };
  }

  /* ── Pricing ──────────────────────────────────────────────────────
     Every number lives in assets/ee-pricing.js (window.EEPricing), which also
     drives the pricing guide page and the Shopify variant prices. Nothing here
     hardcodes a figure — if a price is wrong, fix it there and re-run
     EE-Ops/render-pricing-snippet.mjs + sync-products.mjs. */
  var P = window.EEPricing;

  function areaCm2(state, fieldId) {
    var w = parseFloat(state[fieldId + '_w']), h = parseFloat(state[fieldId + '_h']);
    if (!(w > 0 && h > 0)) return null;
    var u = state[fieldId + '_unit'] || 'mm';
    var f = u === 'mm' ? 0.1 : u === 'in' ? 2.54 : 1;
    return (w * f) * (h * f);
  }

  // Maps the configurator's colour-amount labels onto the guide's 1-3/4-6/7-9
  // tiers. 12 and 12+ sit outside the published tiers, so they price as 7-9
  // plus the per-extra-colour charge, and display as "from".
  function colourTier(label) {
    if (!label) return { idx: 0, extra: 0, from: false };
    if (label.indexOf('12+') === 0) return { idx: 2, extra: P.EXTRA_COLOUR * 3, from: true };
    if (label.indexOf('12') === 0) return { idx: 2, extra: P.EXTRA_COLOUR * 3, from: false };
    if (label.indexOf('9') === 0) return { idx: 2, extra: 0, from: false };
    if (label.indexOf('6') === 0) return { idx: 1, extra: 0, from: false };
    return { idx: 0, extra: 0, from: false };
  }

  // Generic embroidery estimator.
  // opts: { coverage: '50'|'100'|fn(state), colours: fieldId|null, sizeOf: fn(state)→cm² }
  function embroideryEstimate(state, qty, opts) {
    var area = opts.sizeOf(state);
    if (!area) return null;
    if (P.isQuoteOnly(area)) {
      return { text: 'Over ' + P.QUOTE_ABOVE_CM2 + 'cm² — priced individually on your quote.' };
    }
    var cov = typeof opts.coverage === 'function' ? opts.coverage(state) : opts.coverage;
    var tier = colourTier(opts.colours ? state[opts.colours] : null);
    var specials = state.special || [];
    var from = tier.from;

    var unit = P.unitPrice({
      area: area, coverage: cov, tierIdx: tier.idx,
      backing: state.backing || 'Sew-on', specials: specials
    });

    // Premium felt sew-on has no published price. Price the rest and show "from".
    if (unit === null) {
      from = true;
      unit = P.unitPrice({
        area: area, coverage: cov, tierIdx: tier.idx,
        backing: 'Sew-on', specials: specials
      });
      if (unit === null) return null;
    }
    unit += tier.extra;

    // Set-up is charged below the online minimum and free at or above it.
    var setup = qty >= P.MIN_BUYABLE_QTY ? null : P.setupFee(area);
    var discount = P.batchDiscount(qty);

    return {
      unit: unit,
      setup: setup,
      discount: discount,
      tier: P.batchTier(qty),
      nextTier: P.nextTier(qty),
      total: (unit * qty + (setup || 0)) * (1 - discount),
      from: from
    };
  }

  var NAME_PRESET_AREAS = { 'S (25 × 80mm)': 20, 'M (40 × 120mm)': 48, 'L (60 × 145mm)': 87, 'XL (80 × 160mm)': 128 };
  var PRINTED_NOTE = { text: 'Printed patches are priced individually — add to your quote for an exact price within one working day.' };

  /* ── Buyable variant resolution ───────────────────────────────────────
     Turns configurator state into the SKU of a real Shopify variant, or a
     "blocked" reason explaining why this configuration has to be quoted.
     SKUs are built by EEPricing.sku() so they cannot drift from EE-Ops. */

  var COLOUR_TIER_CODE = {
    '3 colours': 'C13', '6 colours': 'C46', '9 colours': 'C79',
    '12 colours': null, '12+ colours': null, // outside the published tiers
  };

  function buyableBlock(state, area) {
    if (!area) return 'incomplete';
    if ((state.special || []).length) return 'special';
    if (state.backing === 'Premium felt sew-on') return 'felt';
    if (state.merrowed === 'Yes') return 'merrowed'; // unpriced — see approval sheet
    if (P.isQuoteOnly(area)) return 'over-max';
    if (!P.bucketFor(area)) return 'over-buyable';
    return null;
  }

  function embroideredResolver(coverage) {
    return function (state) {
      var area = areaCm2(state, 'size');
      var blocked = buyableBlock(state, area);
      if (blocked) return { blocked: blocked };
      var tier = COLOUR_TIER_CODE[state.colours || '3 colours'];
      if (!tier) return { blocked: 'colours' };
      return { sku: P.sku(coverage, P.bucketFor(area).code, tier, state.backing || 'Sew-on') };
    };
  }

  function sizeText(state, id) {
    var w = state[id + '_w'], h = state[id + '_h'];
    if (!(w && h)) return '';
    return w + ' × ' + h + ' ' + (state[id + '_unit'] || 'mm');
  }

  function embroideredProps(state) {
    return {
      'Exact size': sizeText(state, 'size'),
      'Fabric colour': state.fabric,
      'Merrowed border': state.merrowed,
      'Notes': state.notes,
    };
  }

  /* Name patches: fixed presets only. XL is 128cm², above the online ceiling
     and on the far side of the unconfirmed 125cm² price jump, so it quotes. */
  var NAME_PRESET_CODE = { 'S (25 × 80mm)': 'S', 'M (40 × 120mm)': 'M', 'L (60 × 145mm)': 'L' };

  function namePatchResolver(state) {
    if (state.presetSize === 'Custom') return { blocked: 'incomplete' };
    var code = NAME_PRESET_CODE[state.presetSize];
    if (!code) return { blocked: 'over-buyable' };
    var area = NAME_PRESET_AREAS[state.presetSize];
    var blocked = buyableBlock(state, area);
    if (blocked) return { blocked: blocked };
    var cov = (state.coverage || '').indexOf('100%') === 0 ? '100' : '50';
    return { sku: ['NAME', code, cov, P.BACKING_CODES[state.backing || 'Sew-on']].join('-') };
  }

  /* ── Printed ──────────────────────────────────────────────────────────
     Ben, 2026-09-03: printed patches are 50p cheaper and otherwise follow the
     same structure. No colour tier or coverage - DTF is full colour. */

  function printedArea(state) {
    if (state.shape === 'Square')    return P.PRINTED_PRESET_AREAS[state.sizeSq];
    if (state.shape === 'Circle')    return P.PRINTED_PRESET_AREAS[state.sizeCirc];
    if (state.shape === 'Rectangle') return P.PRINTED_PRESET_AREAS[state.sizeRect];
    return null;
  }

  function printedEstimate(state, qty, sizeOf) {
    var area = sizeOf(state);
    if (!area) return null;
    if (P.isQuoteOnly(area)) {
      return { text: 'Over ' + P.QUOTE_ABOVE_CM2 + 'cm² — priced individually on your quote.' };
    }
    var from = false;
    var unit = P.printedUnitPrice({ area: area, backing: state.backing || 'Sew-on' });
    if (unit === null) {
      from = true; // premium felt has no published price
      unit = P.printedUnitPrice({ area: area, backing: 'Sew-on' });
      if (unit === null) return null;
    }
    var setup = qty >= P.MIN_BUYABLE_QTY ? null : P.setupFee(area);
    var discount = P.batchDiscount(qty);
    return {
      unit: unit, setup: setup, discount: discount,
      tier: P.batchTier(qty), nextTier: P.nextTier(qty),
      total: (unit * qty + (setup || 0)) * (1 - discount), from: from
    };
  }

  function printedResolver(sizeOf) {
    return function (state) {
      var area = sizeOf(state);
      if (!area) return { blocked: 'incomplete' };
      if (state.backing === 'Premium felt sew-on') return { blocked: 'felt' };
      if (state.merrowed === 'Yes') return { blocked: 'merrowed' };
      if (P.isQuoteOnly(area)) return { blocked: 'over-max' };
      var bucket = P.bucketFor(area);
      if (!bucket) return { blocked: 'over-buyable' };
      return { sku: P.printedSku(bucket.code, state.backing || 'Sew-on') };
    };
  }

  function printedCustomProps(state) {
    return {
      'Exact size': sizeText(state, 'size'),
      'Fabric colour': state.fabric,
      'Merrowed border': state.merrowed,
      'Notes': state.notes,
    };
  }

  function printedSetProps(state) {
    return {
      'Shape': state.shape,
      'Size': state.shape === 'Square' ? state.sizeSq
            : state.shape === 'Circle' ? state.sizeCirc : state.sizeRect,
      'Notes': state.notes,
    };
  }

  function namePatchProps(state) {
    return {
      'Text': state.text,
      'Font': state.font,
      'Shape': state.shape,
      'Fabric colour': state.fabric,
      'Background colour': state.background,
      'Text thread colour': state.thread,
      'Notes': state.notes,
    };
  }


  /* Price lists we do not have yet. Until Ben supplies these, the products they
     belong to stay quote-only and must NOT be made buyable. */
  var PRINTED_BANDS = null; // TBC — printed and set-size printed patches
  var HYBRID_BANDS  = null; // TBC — hybrid print + embroidery
  var CAP_PRICES    = null; // TBC — cap embroidery
  var BAGTAG_PRICES = null; // TBC — bag tags & lanyards

  /* ── Product definitions ────────────────────────────────────────── */

  var PRODUCTS = {

    'custom-50': {
      id: 'custom-50',
      buyable: { resolve: embroideredResolver('50'), lineItemProps: embroideredProps },
      title: 'Custom 50% Embroidered Patch',
      image: CDN + 'Emporium_Embroidery_Patch50_centered.png?v=1787184262',
      caption: '50% Embroidery — uses fabric for the base of the patch with embroidery on top.',
      note: MERROWED_NOTE,
      defaultQty: 50,
      estimator: function (state, qty) {
        return embroideryEstimate(state, qty, { coverage: '50', colours: 'colours', sizeOf: function (s) { return areaCm2(s, 'size'); } });
      },
      fields: [
        { id: 'size', type: 'size', label: 'Size', hint: '(cost is calculated based on area L × H)', defaultUnit: 'mm', required: true },
        { id: 'fabric', type: 'swatches', label: 'Fabric Colour', options: FABRICS, visible: 7, required: true },
        { id: 'backing', type: 'select', label: 'Type of Backing', options: BACKINGS, default: 'Sew-on' },
        { id: 'colours', type: 'pills', label: 'Amount of Colours', options: COLOUR_AMOUNTS, default: '3 colours' },
        { id: 'special', type: 'checks', label: 'Any Specialist Thread?', options: SPECIAL_THREADS },
        merrowedField(),
        uploadField(),
        notesField(),
      ],
    },

    'custom-100': {
      id: 'custom-100',
      buyable: { resolve: embroideredResolver('100'), lineItemProps: embroideredProps },
      title: 'Custom 100% Embroidered Patch',
      image: CDN + 'FOX_SHIELD_100_centered.png?v=1787184295',
      caption: '100% Embroidery — fully embroidered with no fabric base showing.',
      note: MERROWED_NOTE,
      defaultQty: 50,
      estimator: function (state, qty) {
        return embroideryEstimate(state, qty, { coverage: '100', colours: 'colours', sizeOf: function (s) { return areaCm2(s, 'size'); } });
      },
      fields: [
        { id: 'size', type: 'size', label: 'Size', hint: '(cost is calculated based on area L × H)', defaultUnit: 'mm', required: true },
        { id: 'backing', type: 'select', label: 'Type of Backing', options: BACKINGS, default: 'Sew-on' },
        { id: 'colours', type: 'pills', label: 'Amount of Colours', options: COLOUR_AMOUNTS, default: '3 colours' },
        { id: 'special', type: 'checks', label: 'Any Specialist Thread?', options: SPECIAL_THREADS },
        merrowedField(),
        uploadField(),
        notesField(),
      ],
    },

    'name-patch': {
      id: 'name-patch',
      buyable: { resolve: namePatchResolver, lineItemProps: namePatchProps },
      title: 'Name Patches',
      image: CDN + 'name_badge50_centered.png?v=1787184392',
      caption: 'Custom embroidered name patches — perfect for uniforms and workwear.',
      defaultQty: 50,
      estimator: function (state, qty) {
        return embroideryEstimate(state, qty, {
          coverage: function (s) { return (s.coverage || '').indexOf('100%') === 0 ? '100' : '50'; },
          colours: null, // name patch text: 1–3 colours tier included
          sizeOf: function (s) {
            if (s.presetSize === 'Custom') return areaCm2(s, 'size');
            return NAME_PRESET_AREAS[s.presetSize] || null;
          },
        });
      },
      fields: [
        { id: 'presetSize', type: 'pills', label: 'Size', options: ['S (25 × 80mm)', 'M (40 × 120mm)', 'L (60 × 145mm)', 'XL (80 × 160mm)', 'Custom'], default: 'M (40 × 120mm)', full: true },
        { id: 'size', type: 'size', label: 'Custom Size', hint: '(L × H)', defaultUnit: 'mm', showIf: { field: 'presetSize', equals: 'Custom' }, required: true },
        { id: 'coverage', type: 'radio', label: 'Coverage', options: ['50% (fabric base)', '100% (full embroidery)'], default: '50% (fabric base)' },
        { id: 'fabric', type: 'swatches', label: 'Fabric Colour', hint: 'if 50%', options: FABRICS, visible: 7, showIf: { field: 'coverage', equals: '50% (fabric base)' }, required: true },
        { id: 'background', type: 'swatches', label: 'Background Colour', hint: 'if 100%', options: THREADS, visible: 7, showIf: { field: 'coverage', equals: '100% (full embroidery)' }, required: true },
        { id: 'font', type: 'select', label: 'Font', options: FONTS, default: 'Block' },
        { id: 'shape', type: 'shapes', label: 'Shape', options: ['Square', 'Rectangle', 'Circle', 'Custom'], default: 'Rectangle' },
        { id: 'thread', type: 'swatches', label: 'Text Thread Colour', hint: 'matched to closest in-stock thread', options: THREADS, visible: 8, required: true },
        { id: 'special', type: 'checks', label: 'Special Threads', options: SPECIAL_THREADS },
        { id: 'backing', type: 'select', label: 'Type of Backing', options: BACKINGS, default: 'Sew-on' },
        { id: 'text', type: 'text', label: 'Text', hint: '(what text is needed in the patch)', placeholder: 'e.g. ALEXANDER', required: true, full: true },
        notesField('Change shape, anything specific…'),
      ],
    },

    'printed-custom': {
      id: 'printed-custom',
      buyable: { resolve: printedResolver(function (s) { return areaCm2(s, 'size'); }), lineItemProps: printedCustomProps },
      title: 'Custom Printed Patch',
      image: CDN + 'Printed_Patch_DTF_50_centered.png?v=1787184418',
      caption: 'Full-colour printed patches — ideal for detailed or photographic artwork.',
      note: MERROWED_NOTE,
      defaultQty: 50,
      estimator: function (state, qty) {
        return printedEstimate(state, qty, function (s) { return areaCm2(s, 'size'); });
      },
      fields: [
        { id: 'size', type: 'size', label: 'Size', hint: '(cost is calculated based on area L × H)', defaultUnit: 'mm', required: true },
        { id: 'fabric', type: 'swatches', label: 'Fabric Colour', options: FABRICS, visible: 7, required: true },
        { id: 'backing', type: 'select', label: 'Type of Backing', options: BACKINGS, default: 'Sew-on' },
        merrowedField(),
        uploadField(),
        notesField(),
      ],
    },

    'printed-set': {
      id: 'printed-set',
      buyable: { resolve: printedResolver(printedArea), lineItemProps: printedSetProps },
      title: 'Set Size / Shape Printed Patch',
      image: CDN + 'serpents_isle_centered.png?v=1788533590',
      caption: 'Printed patches in fixed shapes and sizes for fast turnaround.',
      defaultQty: 50,
      estimator: function (state, qty) { return printedEstimate(state, qty, printedArea); },
      fields: [
        { id: 'shape', type: 'shapes', label: 'Shape', options: ['Square', 'Rectangle', 'Circle'], default: 'Rectangle' },
        { id: 'sizeRect', type: 'pills', label: 'Size', options: ['S (25 × 80mm)', 'M (40 × 120mm)', 'L (60 × 145mm)', 'XL (80 × 160mm)'], default: 'M (40 × 120mm)', showIf: { field: 'shape', equals: 'Rectangle' } },
        { id: 'sizeSq', type: 'pills', label: 'Size', options: ['XS (25 × 25mm)', 'S (50 × 50mm)', 'M (75 × 75mm)', 'L (100 × 100mm)', 'XL (125 × 125mm)'], default: 'M (75 × 75mm)', showIf: { field: 'shape', equals: 'Square' } },
        { id: 'sizeCirc', type: 'pills', label: 'Size', options: ['XS (25mm)', 'S (50mm)', 'M (75mm)', 'L (100mm)', 'XL (125mm)'], default: 'M (75mm)', showIf: { field: 'shape', equals: 'Circle' } },
        { id: 'backing', type: 'select', label: 'Type of Backing', options: BACKINGS, default: 'Sew-on' },
        uploadField(),
        notesField('Change shape, anything specific…'),
      ],
    },

    'hybrid': {
      id: 'hybrid',
      // Ben priced printed patches, not hybrids. A hybrid contains embroidery,
      // so it must not inherit the cheaper printed rate - quote until confirmed.
      buyable: null,
      title: 'Hybrid Print / Embroidery Patch',
      image: CDN + 'Smooth_Sesh100_centered.png?v=1787184446',
      caption: 'The best of both — printed detail combined with raised embroidery.',
      defaultQty: 50,
      estimator: function () { return PRINTED_NOTE; },
      fields: [
        { id: 'fabric', type: 'swatches', label: 'Fabric Choice', options: FABRICS, visible: 7, required: true },
        { id: 'size', type: 'size', label: 'Size', hint: '(cost is calculated based on area L × H)', defaultUnit: 'mm', required: true },
        uploadField(),
        notesField('Text, change shape, anything specific…'),
      ],
    },

    'caps': {
      id: 'caps',
      title: 'Custom Caps',
      underConstruction: true,
      buyable: null, // no price list from Ben yet — quote only
      caption: 'This area is under construction — please contact us for a cap embroidery quote.',
      ctaLink: '/pages/contact',
    },

    'bag-tags': {
      id: 'bag-tags',
      title: 'Bag Tags & Lanyards',
      underConstruction: true,
      buyable: null, // no price list from Ben yet — quote only
      caption: 'This area is under construction — please contact us for a quote.',
      ctaLink: '/pages/contact',
    },
  };

  /* ── Upload hardening (ported from the Namedroppers loader) ─────── */

  var UPLOAD_TYPES = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };
  function safeName(name) { return String(name).replace(/[^\w.\-]+/g, '_'); }
  function contentTypeOf(name) {
    var ext = (name.split('.').pop() || '').toLowerCase();
    return UPLOAD_TYPES[ext] || null;
  }

  /* ── Submission ─────────────────────────────────────────────────── */

  function specText(item) {
    var lines = item.summary.map(function (s) { return s.label + ': ' + s.value; });
    if (item.estimate && item.estimate.unit) {
      lines.push('Guide estimate: ' + (item.estimate.from ? 'from ' : '') + '£' + item.estimate.unit.toFixed(2)
        + '/patch, total ~£' + item.estimate.total.toFixed(2)
        + (item.estimate.setup ? ' (incl. £' + item.estimate.setup.toFixed(2) + ' setup)' : ''));
    }
    return '[' + item.productTitle + ' × ' + item.qty + ']\n' + lines.join('\n');
  }

  window.EEConfiguratorConfig = {
    captchaSiteKey: '0x4AAAAAADCcgsf5lB8QTML4', // Live key — hostnames managed in Cloudflare (same as quote tool)
    products: PRODUCTS,

    onSubmit: async function (data) {
      var db = supabase.createClient(SUPABASE_URL, SUPABASE_KEY);

      // ONE quote row per submission, however many items were configured —
      // customers must get a single quote (and a single email thread).
      var quoteId = crypto.randomUUID();
      var quoteIds = [quoteId];
      var first = data.items[0];
      var st = first.state || {};
      var single = data.items.length === 1;
      var totalQty = data.items.reduce(function (s, it) { return s + it.qty; }, 0);
      var width = single ? (parseFloat(st.size_w) || null) : null;
      var height = single ? (parseFloat(st.size_h) || null) : null;
      var allNotes = data.items.map(specText).join('\n\n');

      // Built here, but written by the server after it verifies the Turnstile
      // token. The browser can no longer create a quote row on its own.
      var row = {
        first_name: data.contact.firstName,
        last_name: data.contact.lastName,
        email: data.contact.email,
        phone: data.contact.phone || null,
        deadline: data.contact.deadline || null,
        quantity: String(totalQty),
        coverage: single ? first.productTitle : data.items.length + ' items: ' + data.items.map(function (it) { return it.productTitle; }).join(' + '),
        width: width, height: height,
        unit: width || height ? (st.size_unit || 'mm') : null,
        backing: single ? (st.backing || null) : null,
        border_style: single && st.merrowed ? 'Merrowed border: ' + st.merrowed : null,
        patch_style: single ? (st.shape || first.productId) : null,
        colors: single ? (st.colours || st.thread || null) : null,
        notes: allNotes.slice(0, 4000),
      };

      var filePaths = [];
      for (var idx = 0; idx < data.items.length; idx++) {
        var item = data.items[idx];
        for (var f = 0; f < (item.files || []).length; f++) {
          var file = item.files[f];
          var ct = contentTypeOf(file.name);
          if (!ct) continue; // allow-list only — never trust file.type
          var path = quoteId + '/' + Date.now() + '_' + safeName(file.name);
          var up = await db.storage.from('quote-files').upload(path, file, { upsert: false, contentType: ct });
          if (up.error) throw new Error('File upload failed: ' + up.error.message);
          filePaths.push(path);
        }
      }
      if (filePaths.length) row.file_paths = filePaths;

      // Server-side: verifies the Turnstile token with Cloudflare, then writes
      // the row and emails the office. Nothing is saved if the check fails.
      var res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quoteId: quoteId,
          turnstileToken: data.turnstileToken,
          turnstileClientReason: data.turnstileClientReason || null,
          hp: data.hp || '',
          quote: row,
        }),
      });
      if (!res.ok) {
        var err = await res.json().catch(function () { return {}; });
        if (res.status === 429) {
          throw new Error('Too many requests from your connection — please wait a few minutes and try again, or call us on 020 3432 5550.');
        }
        throw new Error(err.error === 'Verification failed'
          ? 'Could not verify you are human — please reload the page and try again.'
          : (err.error || 'Could not send your quote. Please try again.'));
      }
    },
  };
})();
